import angular from 'angular';
import dropdown from '../dropdown';
import autocompleteDirective from './autocomplete-directive';
import indeterminateProgress from '../indeterminate-progress';

/**
 * @ngdoc module
 * @name akamai.components.autocomplete
 *
 * @requires akamai.components.dropdown
 *
 * @description
 * Autocomplete is a feature that enables users to quickly find and select information from a
 * pre-populated list. As characters are entered, the control displays matching options in a
 * dropdown menu. The autocomplete control is customizable to show results after one or more
 * characters are entered. Users also have the ability to clear a selection. Also, the
 * `<akam-autocomplete-selected>` and `<akam-autocomplete-option>` tags can be used to declare
 * custom markup for the select item and option items respectively.
 *
 * @guideline https://design.akamai.com/components/autocomplete/
 *
 * @example index.html
 * <akam-autocomplete ng-model="vm.selectedItem"
 *                    on-search="fn(searchTerm)"
 *                    text-property="name"
 *                    key-property="itemId"
 *                    minimum-search="0"
 *                    placeholder="an.i18n.key"
 *                    is-disabled="vm.isDisabled"
 *                    is-readonly="vm.isReadonly"
 *                    no-filter-results-message="custom.no.filter.results.i18n.key"
 *                    item-failure-message="custom.item.failure.i18n.key"
 *                    appended-to-body
 *                    clearable
 *                    autofocus>
 *
 *     <akam-autocomplete-selected>
 *         custom: {% raw %}{{autocomplete.selectedItem.name}}{% endraw %}
 *     </akam-autocomplete-selected>
 *
 *     <akam-autocomplete-option>
 *       <span>custom: {% raw %}{{item.name}}{% endraw %}</span>
 *     </akam-autocomplete-option>
 *
 * </akam-autocomplete>
 *
 */
export default angular.module('akamai.components.autocomplete', [
  dropdown.name,
  indeterminateProgress.name
])

/**
 * @ngdoc directive
 *
 * @name akamAutocomplete
 *
 * @description Creates an autocomplete control
 *
 * @restrict E
 *
 * @param {(Object|string)} ng-model The autocomplete's selected item.
 *
 * @param {function(string): Object[]|string[]|Promise} on-search A function that will be called
 * when the input changes. The values returned will be used to populate the dropdown menu.
 * The function will be passed `searchTerm` as an argument. The function can return a `Promise` or
 * an `Array` of either objects or strings.
 *
 * @param {string} [text-property] This attribute is only applicable and required when search
 * results are objects. It defines the property used for rendering the selected item and search
 * results. The search result values must be unique. `text-property` can be nested and will be
 * parsed accordingly (e.g. `text-property="foo.bar.label"`).
 *
 * @param {string} [key-property] This attribute is only applicable when search results are
 * objects. If `key-property` is defined, the corresponding property for the selected object is
 * bound to `ng-model`. If `key-property` is not defined, the selected object itself is bound to
 * `ng-model`.
 *
 * @param {string} [disabled-property] This attribute is only applicable when search results are
 * objects. If `disabled-property` is defined, any menu items that have the corresponding property
 * set to the boolean true or a function which returns the boolean true will have a css class
 * defined as 'disabled-dropdown-item' on its wrapping anchor tag. Additionally clicking on any
 * of these disabled items will not change the selected item.
 *
 * @param {number} [minimum-search=1] The minimum number of characters required to search.
 * The default value is 0 which calls `on-search` when the search field is focused.
 *
 * @param {string|TranslateKey} [placeholder=Select one] The placeholder text for the search field.
 * When using custom markup, include `{{autocomplete.placeholder}}` to display the default
 * placeholder text.
 *
 * ```
 * <akam-autocomplete>
 *   <akam-autocomplete-selected>
 *     {% raw %}{{autocomplete.placeholder}}{% endraw %}
 *    </akam-autocomplete-selected>
 * </akam-autocomplete>
 * ```
 *
 * @param {boolean} [is-disabled=false] If disabled, no user interaction will be possible.
 *
 * @param {boolean} [is-readonly=false] If readonly, no user interaction will be possible but the
 * text will be easier to read.
 *
 * @param {*} [appended-to-body] If present, the dropdown menu will be appended to the body. Use
 * this when using autocomplete in a container that is set to `overflow: hidden`.
 *
 * @param {*} [clearable] If present, the selected item will be clearable.
 *
 * @param {*} [autofocus] If present, the search field will be auto-focused.
 *
 * @param {function(Event)} [on-blur] Callback that is invoked whenever autocomplete
 * is blured.
 *
 * @param {string|TranslateKey} [item-failure-message=components.autocomplete.itemFailureMessage]
 * The text to show the user when the items property fails to load (promise is rejected). If not
 * set, the directive uses the default akamai-core item failure message.
 *
 * @param {string|TranslateKey} [no-filter-results-message=components.autocomplete.noFilterResults]
 * The text to show the user when the filter text entered by the user does not produce any results.
 * If not set, the directive uses the default akamai-core no filter results message.
 *
 */
.directive('akamAutocomplete', autocompleteDirective);
